import { track } from "@ignite-analytics/track";
import { Box, Switch, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import { useRefetchAssessment } from "../../hooks/useAssessmentDetail";
import useToken from "../../hooks/useToken";
import { useAlert } from "../../lib/Alerts";
import { authHeader, formsFetch, unwrap } from "../../lib/formsApi";

type Props = {
    assessment: { id: string; dataSharingConsent: boolean };
    disabled?: boolean;
};

const SharingConsentToggle: React.FC<Props> = ({ assessment, disabled = false }) => {
    const refetchAssessment = useRefetchAssessment();
    const { alertUser } = useAlert();
    const { token } = useToken();
    const [pendingConsent, setPendingConsent] = useState<boolean | null>(null);

    const handleConsentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newConsent = event.target.checked;
        setPendingConsent(newConsent);
        try {
            unwrap(
                await formsFetch.PUT("/api/ast/{id}/sharing-consent", {
                    params: { path: { id: assessment.id } },
                    headers: authHeader(token),
                    body: { consent: newConsent },
                }),
                "update sharing consent"
            );
            await refetchAssessment();
            track("toggled sharing consent", { consent: newConsent });
        } catch (err) {
            Sentry.captureException(err);
            alertUser({
                value: <FormattedMessage defaultMessage="Could not save your consent. Please try again." />,
                severity: "error",
            });
        } finally {
            setPendingConsent(null);
        }
    };

    return (
        <Box
            sx={{
                mb: 3,
                p: 2,
                display: "flex",
                alignItems: "flex-start",
                backgroundColor: "grey.50",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
            }}
        >
            <Switch
                checked={pendingConsent ?? assessment.dataSharingConsent}
                onChange={handleConsentChange}
                disabled={disabled || pendingConsent !== null}
                sx={{ mt: 1, mr: 2 }}
            />
            <Box>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                    <FormattedMessage defaultMessage="Share once to limit similar requests from others." />
                </Typography>
                <Typography variant="body1">
                    <FormattedMessage
                        defaultMessage="I consent to the company documents and certificates I upload being shared and reused
                        across Ignite's customer base. While a document is still valid, Ignite won't ask you to submit it
                        again, so you're not re-sending the same certificate every time a new customer needs it."
                    />
                </Typography>
            </Box>
        </Box>
    );
};

export default SharingConsentToggle;
